import React from "react";

import { SkillCheckLogo, SkillPanelLogo } from "@/assets/images";
import { getTypedKeys, sortStrings } from "@/lib/lang";
import { PermissionDefinitionDto, PermissionScope, Product, UserDetailsDto } from "@/types/auth";

type ProductMeta = {
  key: Product;
  name: string;
  logo: React.ReactNode | null;
};

type ProductMap = {
  [key in Product]: ProductMeta;
};

export const allProducts: ProductMap = {
  AUTH: { key: "AUTH", name: "UserManager", logo: null },
  BOOST: { key: "BOOST", name: "SkillPanel", logo: <SkillPanelLogo height={16} /> },
  SCORE: { key: "SCORE", name: "SkillCheck", logo: <SkillCheckLogo height={16} /> },
  SUBSCRIPTION: { key: "SUBSCRIPTION", name: "Subscriptions", logo: null },
};

/** The products an admin can enable for a user; SUBSCRIPTION is internal and left to support. */
export const customerFacingProducts: Product[] = ["AUTH", "BOOST", "SCORE"];

/** The product as the admin sees it on the product tiles: "UserManager", "SkillPanel", "SkillCheck". */
export const productDisplayName = (product: Product) => allProducts[product]?.name ?? product;

/** "UserManager", "UserManager and SkillCheck", "UserManager, SkillPanel and SkillCheck". */
export const listProductNames = (products: Product[]) => {
  const names = products.map(productDisplayName);
  return names.length < 2 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
};

export const permissionScopeCopyMap: { [key in PermissionScope]: string } = {
  // The words of a role's "Applies to", so a permission's scope never reads as a different setting
  GLOBAL: "Whole company",
  SELF_CHILDREN: "Their teams",
  DIRECT_SUPERVISOR: "Their direct reports",
};

export const assignProductPermissions = (permissions: PermissionDefinitionDto[]) =>
  getTypedKeys(allProducts).reduce(
    (acc, cur) => {
      const product = {
        ...allProducts[cur],
        permissions: permissions
          .filter((p) => p.permission.product === cur)
          .sort((a, b) => sortStrings(a.permission.domain, b.permission.domain)),
      };
      acc.push(product);
      return acc;
    },
    [] as (ProductMeta & { permissions: PermissionDefinitionDto[] })[],
  );

/** Bare `product:role` authorities such as `auth:support` — no domain, no operation, no scope. */
export const isTechnicalPermission = (p: string) => p.split(":").length === 2;

type UserPermissionsGrouped = {
  permissions: UserDetailsDto["permissions"];
  technicalPermissions: UserDetailsDto["permissions"];
};
/**
 * Separates technical permissions from regular ones.
 *
 * @param permissions All permissions of a user.
 */
export const separateTechnicalPermissions = (permissions: UserDetailsDto["permissions"]) => {
  return permissions.reduce<UserPermissionsGrouped>(
    (acc, curr) => {
      if (isTechnicalPermission(curr)) {
        acc.technicalPermissions.push(curr);
      } else {
        acc.permissions.push(curr);
      }
      return acc;
    },
    {
      permissions: [],
      technicalPermissions: [],
    },
  );
};
